import React from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useWindowSize } from '../../../hooks';
import { useToast } from '../../ui/ToastManager';
import { formatDateBR } from '../../../utils/dateUtils';

/**
 * Modal para bloqueio de horários
 * 
 * @param {Object} props - Propriedades do componente
 * @param {boolean} props.isOpen - Se o modal está aberto
 * @param {Object} props.blockingData - Dados do bloqueio
 * @param {Function} props.onClose - Função chamada ao fechar o modal
 * @param {Function} props.onSave - Função chamada ao salvar o bloqueio
 * @param {Function} props.onChange - Função para atualizar os dados do bloqueio
 * @param {Array} props.resources - Lista de recursos (profissionais)
 * @param {boolean} props.loading - Indica se está salvando os dados
 * @param {string} props.activeTab - Aba ativa ('new' ou 'list')
 * @param {Function} props.onTabChange - Função chamada ao mudar de aba
 * @param {Array} props.blockings - Lista de bloqueios existentes
 * @param {boolean} props.loadingBlockings - Indica se está carregando bloqueios
 * @param {Function} props.onEdit - Função chamada ao editar um bloqueio
 * @param {Function} props.onDelete - Função chamada ao excluir um bloqueio
 * @param {boolean} props.editMode - Indica se está em modo de edição
 * @param {string} props.validationError - Mensagem de erro de validação
 * @returns {JSX.Element} - Elemento React
 */
const TimeBlockingModal = ({ 
  isOpen, 
  blockingData, 
  onClose, 
  onSave,
  onChange,
  resources,
  loading = false,
  activeTab = 'new',
  onTabChange,
  blockings = [],
  loadingBlockings = false,
  onEdit,
  onDelete,
  editMode = false,
  validationError = ''
}) => {
  // Hook precisa ser chamado antes de qualquer retorno condicional
  const { width } = useWindowSize();
  const isMobile = width < 768;
  
  if (!isOpen) return null;

  const modalClassName = `fixed inset-0 z-50 flex items-center justify-center`;

  const containerClassName = `bg-white rounded-lg shadow-xl overflow-hidden ${
    isMobile ? 'w-[calc(100%-32px)] max-h-[90vh]' : 'max-w-lg w-full mx-4'
  }`;
  
  // Função para atualizar um campo específico
  const handleFieldChange = (field, value) => {
    onChange(field, value);
  };
  
  // Formatar a data para exibição
  const formattedDate = blockingData.date ? 
    format(new Date(blockingData.date), "EEEE, d 'de' MMMM", { locale: ptBR }) : '';
  
  // Renderizar o formulário de bloqueio
  const renderBlockingForm = () => (
    <div className="space-y-4 min-h-[250px]">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Data */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Data
          </label>
          <input
            type="date"
            value={blockingData.date || ''}
            onChange={(e) => onChange('date', e.target.value)}
            className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
            disabled={loading}
          />
        </div>
        
        {/* Profissional */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Profissional
          </label>
          <select
            value={blockingData.resourceId || ''}
            onChange={(e) => onChange('resourceId', e.target.value)}
            className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
            disabled={loading}
          >
            <option value="">Todos os profissionais</option>
            {resources?.map((resource) => (
              <option key={resource.id} value={resource.id}>
                {resource.title || resource.nome}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Hora Início */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Hora Início
          </label>
          <input
            type="time"
            value={blockingData.startTime || ''}
            onChange={(e) => onChange('startTime', e.target.value)}
            className={`w-full border rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
              validationError ? 'border-red-300' : 'border-gray-300'
            }`}
            disabled={loading}
          />
        </div>
        
        {/* Hora Fim */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Hora Fim
          </label>
          <input
            type="time"
            value={blockingData.endTime || ''}
            onChange={(e) => onChange('endTime', e.target.value)}
            className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
            disabled={loading}
          />
        </div>
      </div>
      
      {/* Mensagem de validação */}
      {validationError && (
        <div className="text-red-500 text-sm mt-1">
          {validationError}
        </div>
      )}
      
      {/* Motivo */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Motivo
        </label>
        <input
          type="text"
          value={blockingData.title || ''}
          onChange={(e) => onChange('title', e.target.value)}
          placeholder="Ex: Almoço, Reunião, Folga, etc."
          className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
          disabled={loading}
        />
      </div>
    </div>
  );
  
  // Função para obter o nome do profissional pelo ID
  const getProfessionalName = (profId) => {
    if (!profId) return 'Todos os profissionais';
    
    const prof = resources?.find(r => r.id === profId);
    return prof ? (prof.title || prof.nome) : 'Profissional não encontrado';
  };
  
  // Renderizar a lista de bloqueios
  const renderBlockingsList = () => (
    <div className="space-y-4 min-h-[250px]">
      {loadingBlockings ? (
        <div className="py-4 text-center text-gray-500">
          Carregando bloqueios...
        </div>
      ) : blockings.length === 0 ? (
        <div className="py-4 text-center text-gray-500">
          Nenhum bloqueio de horário encontrado
        </div>
      ) : (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {blockings.map((blocking) => (
            <div 
              key={blocking.id} 
              className="border border-gray-200 rounded-md p-3 hover:bg-gray-50"
            >
              <div className="flex justify-between items-start mb-2">
                <div className="font-medium text-gray-800">{blocking.titulo}</div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => onEdit(blocking)}
                    className="text-blue-600 hover:text-blue-800 p-1"
                    disabled={loading}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => onDelete(blocking.id)}
                    className="text-red-600 hover:text-red-800 p-1"
                    disabled={loading}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="text-sm text-gray-600">
                {formatDateBR(blocking.data)} • {blocking.horarioInicio} - {blocking.horarioFim}
              </div>
              
              <div className="text-xs text-gray-500 mt-1">
                {getProfessionalName(blocking.profissionalId)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
  
  return (
    <div className={modalClassName}>
      <div className="absolute inset-0 bg-black bg-opacity-25" onClick={onClose}></div>
      
      <div className={containerClassName} style={{ zIndex: 1 }}>
        <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex justify-between items-center">
          <h3 className="text-lg font-medium text-blue-800">
            {editMode ? 'Editar Bloqueio' : 'Bloquear Horário'}
          </h3>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-800 focus:outline-none"
            aria-label="Fechar"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        
        {/* Abas */}
        <div className="flex border-b border-gray-200">
          <button
            className={`flex-1 py-3 px-4 text-center ${
              activeTab === 'new' 
                ? 'border-b-2 border-blue-500 text-blue-600 font-medium' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => onTabChange('new')}
          >
            {editMode ? 'Editar Bloqueio' : 'Novo Bloqueio'}
          </button>
          <button
            className={`flex-1 py-3 px-4 text-center ${
              activeTab === 'list' 
                ? 'border-b-2 border-blue-500 text-blue-600 font-medium' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => onTabChange('list')}
          >
            Bloqueios
          </button>
        </div>
        
        <div className="p-4">
          {activeTab === 'new' ? renderBlockingForm() : renderBlockingsList()}
          
          {activeTab === 'new' && (
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  console.log("Botão Bloquear/Atualizar clicado");
                  if (typeof onSave === 'function') {
                    console.log("Chamando onSave");
                    onSave();
                  } else {
                    console.log("onSave não é uma função");
                  }
                }}
                className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                  loading || validationError
                    ? 'bg-blue-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
                disabled={loading || !!validationError || !blockingData.title}
                style={{ position: 'relative', zIndex: 10 }}
              >
                {loading ? 'Salvando...' : (editMode ? 'Atualizar' : 'Bloquear')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimeBlockingModal; 